// HMAC-SHA256 signer for the public Live Wall endpoint.
//
// SECURITY MODEL
//   The Live Wall URL is the credential — anyone who can read it can view
//   the wall. To make direct API scraping (`curl`) noticeably harder, the
//   frontend attaches a signature header that the backend re-computes:
//
//     canonical = `live-wall.v1.get.${token}`
//     signature = hex(HMAC_SHA256(secret, canonical))
//     Header    = X-Live-Wall-Signature: <signature>
//
//   Limitations to be honest about:
//     • The secret is bundled into the JS, so a determined attacker who
//       downloads the bundle can compute signatures themselves. This
//       pattern only raises the bar against casual scraping — it does not
//       stop a sophisticated attacker.
//     • The signature is static for a given (secret, token) pair. Replay
//       protection (timestamp window, nonce) is intentionally out of
//       scope for v1.
//
// PROTOCOL VERSION
//   The `v1` segment in the canonical string lets us rotate the protocol
//   without rotating the secret. To migrate, bump to `v2.get` and the
//   backend will reject all `v1` signatures until both sides upgrade.

/**
 * Compute the hex-encoded HMAC-SHA256 signature for a Live Wall URL.
 *
 * Returns an empty string when the secret is missing or the URL has no
 * share token. The calling interceptor still attaches the header so the
 * backend can reject with a uniform 401 instead of a different error
 * shape — letting the existing `PublicApiError` flow surface it.
 *
 * @param {string} url      Full request URL (e.g. `/api/public/live-wall/<token>`).
 * @param {string} secret   Shared HMAC secret.
 * @returns {Promise<string>} Hex-encoded signature, or '' on missing inputs.
 */
export async function signLiveWallUrl(url, secret) {
  if (!secret) return ''
  const token = extractLiveWallToken(url)
  if (!token) return ''

  const canonical = `live-wall.v1.get.${token}`
  const keyBytes = new TextEncoder().encode(secret)
  const dataBytes = new TextEncoder().encode(canonical)

  // Web Crypto API — available in every browser we target (and in
  // jsdom-based test runners). `importKey` with `extractable: false`
  // means the key handle can't be exfiltrated from the signing context.
  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  const sigBuffer = await crypto.subtle.sign('HMAC', key, dataBytes)

  return bufferToHex(sigBuffer)
}

/**
 * Pull the share token out of a Live Wall URL.
 *
 * Accepts both absolute URLs (`http://host/api/public/live-wall/<tok>`)
 * and relative paths (`/api/public/live-wall/<tok>`). Matches the same
 * shape `backend/routes` exposes — the segment after the last
 * `/live-wall/` in the path.
 */
export function extractLiveWallToken(url) {
  if (!url || typeof url !== 'string') return ''
  // Strip query string and fragment first.
  const cleanPath = url.split('?')[0].split('#')[0]
  // Match `/live-wall/<token>` anywhere in the path (not just at the end),
  // so the helper also works if the path is e.g. `/api/public/live-wall/<tok>`.
  const match = cleanPath.match(/\/live-wall\/([^/]+)\/?$/)
  return match ? match[1] : ''
}

function bufferToHex(buffer) {
  const bytes = new Uint8Array(buffer)
  let hex = ''
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0')
  }
  return hex
}

export const LIVE_WALL_SIGNATURE_HEADER = 'X-Live-Wall-Signature'
